"use client";

import { useEffect, useId, useState, type KeyboardEvent } from "react";
import { useSearchParams } from "next/navigation";
import {
  ChevronRightIcon,
  CloseIcon,
  LogsIcon,
  PlayIcon,
  SearchIcon,
  StopIcon,
} from "@/components/icons";
import { Select } from "@/components/ui/Select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { TaskStatus, type Task as TaskInfo } from "@/contracts/tasks";
import { formatTaskTimestamp } from "../model/task-timestamp";
import styles from "./TaskWorkspace.module.css";

type TaskListProps = {
  tasks: readonly TaskInfo[];
  pendingTaskId?: string;
  onViewLogs: (taskId: string) => void;
  onRunTask: (taskId: string) => void;
  onCancelTask: (taskId: string) => void;
};

const taskStatuses = Object.values(TaskStatus);

function groupTasksByCategory(tasks: readonly TaskInfo[]) {
  return tasks.reduce<Map<string, TaskInfo[]>>((groups, task) => {
    const category = task.id.split(":")[0];
    const categoryTasks = groups.get(category) ?? [];
    categoryTasks.push(task);
    groups.set(category, categoryTasks);
    return groups;
  }, new Map());
}

function formatCategoryName(category: string) {
  return category
    .split(/[-_]/g)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function TaskDate({ value, now }: { value?: string | null; now: number | null }) {
  if (!value) {
    return (
      <>
        <span className={styles.srOnly}>Unavailable</span>
        <span aria-hidden="true">—</span>
      </>
    );
  }

  const formattedDate = now === null ? null : formatTaskTimestamp(value, now);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <time className={styles.taskDate} dateTime={value} tabIndex={0}>
          {formattedDate?.relative ?? "—"}
        </time>
      </TooltipTrigger>
      <TooltipContent>{formattedDate?.exact ?? value}</TooltipContent>
    </Tooltip>
  );
}

function StatusBadge({ status }: { status: TaskStatus }) {
  return (
    <span className={styles.statusBadge} data-status={status.toLowerCase()}>
      <span className={styles.statusMark} aria-hidden="true" />
      {status}
    </span>
  );
}

function TaskTable({
  tasks,
  now,
  category,
  onViewLogs,
  onRunTask,
  onCancelTask,
  pendingTaskId,
}: {
  tasks: readonly TaskInfo[];
  now: number | null;
  category: string;
  onViewLogs: TaskListProps["onViewLogs"];
  onRunTask: TaskListProps["onRunTask"];
  onCancelTask: TaskListProps["onCancelTask"];
  pendingTaskId?: string;
}) {
  return (
    <div
      className={styles.tableScroller}
      role="region"
      tabIndex={0}
      aria-label={`${formatCategoryName(category)} tasks table`}
    >
      <table className={styles.taskTable}>
        <thead>
          <tr>
            <th scope="col">Task ID</th>
            <th scope="col">Status</th>
            <th scope="col">Created</th>
            <th scope="col">Started</th>
            <th scope="col">Completed</th>
            <th scope="col" className={styles.actionsHeading}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.id}>
              <th scope="row" data-label="Task ID">
                <span className={styles.taskId}>{task.id}</span>
              </th>
              <td data-label="Status">
                <StatusBadge status={task.status} />
              </td>
              <td data-label="Created">
                <TaskDate value={task.createdAt} now={now} />
              </td>
              <td data-label="Started">
                <TaskDate value={task.startedAt} now={now} />
              </td>
              <td data-label="Completed">
                <TaskDate value={task.completedAt} now={now} />
              </td>
              <td data-label="Actions" className={styles.actionsCell}>
                <div className={styles.taskActions}>
                  <button
                    className={styles.logsButton}
                    type="button"
                    onClick={() => onViewLogs(task.id)}
                  >
                    <LogsIcon />
                    Logs
                  </button>
                  {task.status === TaskStatus.Running ? (
                    <button
                      className={styles.taskActionButton}
                      data-action="cancel"
                      type="button"
                      aria-label={`Cancel ${task.id}`}
                      title="Cancel task"
                      disabled={pendingTaskId === task.id}
                      onClick={() => onCancelTask(task.id)}
                    >
                      <StopIcon />
                    </button>
                  ) : (
                    <button
                      className={styles.taskActionButton}
                      data-action="run"
                      type="button"
                      aria-label={`Run ${task.id}`}
                      title="Run task"
                      disabled={pendingTaskId === task.id}
                      onClick={() => onRunTask(task.id)}
                    >
                      <PlayIcon />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TaskList({
  tasks,
  pendingTaskId,
  onViewLogs,
  onRunTask,
  onCancelTask,
}: TaskListProps) {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    const refresh = () => setNow(Date.now());
    // Format only in the browser so SSR never supplies a server-local timezone.
    const initialRefresh = window.setTimeout(refresh, 0);
    const interval = window.setInterval(refresh, 30_000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearTimeout(initialRefresh);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);
  const searchInputId = useId();
  const statusFilterId = useId();
  const searchParams = useSearchParams();
  const query = searchParams.get("search") ?? "";
  const selectedStatus = taskStatuses.find((status) => status === searchParams.get("status"));
  const normalizedQuery = query.trim().toLowerCase();
  const hasActiveFilters = Boolean(normalizedQuery || selectedStatus);
  const filteredTasks = tasks.filter(
    (task) =>
      (!normalizedQuery || task.id.toLowerCase().includes(normalizedQuery)) &&
      (!selectedStatus || task.status === selectedStatus),
  );
  const groupedTasks = Array.from(groupTasksByCategory(filteredTasks));

  function updateUrlParam(name: "search" | "status", value: string) {
    const nextUrl = new URL(window.location.href);

    if (value) {
      nextUrl.searchParams.set(name, value);
    } else {
      nextUrl.searchParams.delete(name);
    }

    window.history.replaceState(
      null,
      "",
      `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`,
    );
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape" && query) {
      event.preventDefault();
      updateUrlParam("search", "");
    }
  }

  return (
    <section className={styles.taskList} aria-label="Task list">
      <div className={styles.searchToolbar}>
        <h1 className={styles.taskListTitle}>Tasks</h1>
        <div className={styles.toolbarEnd}>
          <div className={styles.searchField}>
            <span className={styles.searchIcon}>
              <SearchIcon />
            </span>
            <label className={styles.srOnly} htmlFor={searchInputId}>
              Search task IDs
            </label>
            <input
              id={searchInputId}
              className={styles.searchInput}
              type="search"
              placeholder="Search task IDs"
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(event) => updateUrlParam("search", event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            {query ? (
              <button
                className={styles.clearSearchButton}
                type="button"
                aria-label="Clear task search"
                onClick={() => updateUrlParam("search", "")}
              >
                <CloseIcon />
              </button>
            ) : null}
          </div>

          <label className={styles.srOnly} htmlFor={statusFilterId}>
            Filter tasks by status
          </label>
          <Select
            id={statusFilterId}
            className={styles.statusFilter}
            controlSize="compact"
            aria-label="Filter tasks by status"
            value={selectedStatus ?? ""}
            onChange={(event) => updateUrlParam("status", event.target.value)}
          >
            <option value="">All statuses</option>
            {taskStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {hasActiveFilters && filteredTasks.length === 0 ? (
        <div className={styles.noSearchResults} role="status">
          <span className={styles.noSearchResultsIcon}>
            <SearchIcon />
          </span>
          <h2>No matching tasks</h2>
        </div>
      ) : (
        <div className={styles.taskGroups}>
          {groupedTasks.map(([category, categoryTasks], index) => (
            <details
              className={styles.taskGroup}
              key={`${category}:${normalizedQuery}:${selectedStatus ?? "all"}`}
              open={hasActiveFilters ? true : index < 2}
            >
              <summary>
                <span className={styles.disclosureIcon} aria-hidden="true">
                  <ChevronRightIcon />
                </span>
                <span className={styles.categoryName}>{formatCategoryName(category)}</span>
                <span className={styles.categoryCount}>
                  {categoryTasks.length} {categoryTasks.length === 1 ? "task" : "tasks"}
                </span>
              </summary>
              <TaskTable
                tasks={categoryTasks}
                now={now}
                category={category}
                onViewLogs={onViewLogs}
                onRunTask={onRunTask}
                onCancelTask={onCancelTask}
                pendingTaskId={pendingTaskId}
              />
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
